import React, { useMemo, useState } from 'react';
import { Card, Button, Form, Row, Col, Alert, Badge, Accordion, Modal, Table, Spinner, InputGroup } from 'react-bootstrap';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import toast from 'react-hot-toast';
import Pagination from '../../components/common/Pagination';

const HelpSupport = () => {
  const queryClient = useQueryClient();

  const [showContactModal, setShowContactModal] = useState(false);
  const [contactForm, setContactForm] = useState({
    subject: '',
    category: 'general',
    message: '',
    priority: 'medium'
  });

  const [showResponseModal, setShowResponseModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [responseForm, setResponseForm] = useState({
    response: '',
    status: 'in_progress',
    priority: 'medium'
  });

  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    category: '',
    user_type: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const buildQueryParams = () => {
    const params = new URLSearchParams({
      limit: itemsPerPage.toString(),
      offset: ((currentPage - 1) * itemsPerPage).toString(),
      include_stats: '1'
    });

    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        params.append(key, value);
      }
    });

    if (searchTerm) {
      params.append('search', searchTerm);
    }

    return params.toString();
  };

  const { data: ticketsResponse, isLoading: ticketsLoading } = useQuery({
    queryKey: ['admin_support_tickets', filters, searchTerm, currentPage, itemsPerPage],
    queryFn: async () => {
      const response = await apiService.get(`/api/admin/support_tickets?${buildQueryParams()}`);
      return response.data;
    }
  });

  const tickets = ticketsResponse?.data || [];
  const ticketStats = ticketsResponse?.stats || {};
  const totalTickets = ticketsResponse?.total || 0;

  const statusBadgeVariant = (status) => {
    const variants = {
      open: 'secondary',
      in_progress: 'info',
      resolved: 'success',
      closed: 'dark'
    };
    return variants[status] || 'secondary';
  };

  const priorityBadgeVariant = (priority) => {
    const variants = {
      low: 'secondary',
      medium: 'primary',
      high: 'warning',
      urgent: 'danger'
    };
    return variants[priority] || 'secondary';
  };

  // Submit support ticket mutation
  const submitTicketMutation = useMutation({
    mutationFn: async (ticketData) => apiService.post('/api/admin/support-ticket', ticketData),
    onSuccess: () => {
      toast.success('Support ticket submitted successfully!');
      setShowContactModal(false);
      setContactForm({
        subject: '',
        category: 'general',
        message: '',
        priority: 'medium'
      });
      queryClient.invalidateQueries(['admin_support_tickets']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to submit support ticket');
    }
  });

  const updateTicketMutation = useMutation({
    mutationFn: async ({ id, data }) => apiService.put(`/api/admin/support_tickets/${id}`, data),
    onSuccess: () => {
      toast.success('Support ticket updated successfully!');
      queryClient.invalidateQueries(['admin_support_tickets']);
      closeResponseModal();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update support ticket');
    }
  });

  const deleteTicketMutation = useMutation({
    mutationFn: async (id) => apiService.delete(`/api/admin/support_tickets/${id}`),
    onSuccess: () => {
      toast.success('Support ticket deleted successfully!');
      queryClient.invalidateQueries(['admin_support_tickets']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete support ticket');
    }
  });

  const handleContactSubmit = (e) => {
    e.preventDefault();
    submitTicketMutation.mutate(contactForm);
  };

  const handleInputChange = (field, value) => {
    setContactForm(prev => ({ ...prev, [field]: value }));
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  const openResponseModal = (ticket) => {
    setSelectedTicket(ticket);
    setResponseForm({
      response: ticket.response || '',
      status: ticket.status || 'open',
      priority: ticket.priority || 'medium'
    });
    setShowResponseModal(true);
  };

  const closeResponseModal = () => {
    setSelectedTicket(null);
    setResponseForm({ response: '', status: 'in_progress', priority: 'medium' });
    setShowResponseModal(false);
  };

  const handleResponseSubmit = (event) => {
    event.preventDefault();
    if (!selectedTicket) return;

    updateTicketMutation.mutate({
      id: selectedTicket.id,
      data: {
        response: responseForm.response,
        status: responseForm.status,
        priority: responseForm.priority
      }
    });
  };

  const ticketSummary = useMemo(() => ({
    total: ticketStats?.total || 0,
    open: ticketStats?.by_status?.open || 0,
    inProgress: ticketStats?.by_status?.in_progress || 0,
    resolved: ticketStats?.by_status?.resolved || 0,
    closed: ticketStats?.by_status?.closed || 0,
    high: ticketStats?.by_priority?.high || 0,
    urgent: ticketStats?.by_priority?.urgent || 0
  }), [ticketStats]);

  const faqData = [
    {
      category: "Getting Started",
      questions: [
        {
          question: "How do I add a new student?",
          answer: "Navigate to Student Management from the sidebar, click 'Add New Student', fill in the required information including personal details, academic information, and parent details, then click 'Save Student'."
        },
        {
          question: "How do I set up fee structures?",
          answer: "Go to Fee Structures in the sidebar, click 'Add New Structure', select the academic year, grade, and category, set the amount and due date, then save. You can also set up installments if needed."
        },
        {
          question: "How do I generate reports?",
          answer: "Visit the Reports section, select the type of report you need (Fee Collection, Fee Dues, etc.), apply any filters like date range or academic year, then click 'Generate Report' or export to Excel/CSV."
        }
      ]
    },
    {
      category: "Fee Management",
      questions: [
        {
          question: "How do I collect fees from students?",
          answer: "Go to Fee Collection, search for the student by name or roll number, select the fee categories to collect, choose payment method, enter the amount, and generate a receipt."
        },
        {
          question: "How do I handle partial payments?",
          answer: "When collecting fees, you can enter a partial amount instead of the full fee amount. The system will automatically calculate the remaining balance and update the student's fee status."
        },
        {
          question: "How do I set up late fees?",
          answer: "In System Settings > General, you can configure the late fee percentage. This will be automatically applied to overdue payments based on the due date set in fee structures."
        }
      ]
    },
    {
      category: "Reports & Analytics",
      questions: [
        {
          question: "How do I view fee collection reports?",
          answer: "Navigate to Reports, select 'Fee Collection Report', set your date range and filters, then generate the report. You can view it online or export to Excel/CSV format."
        },
        {
          question: "How do I track outstanding fees?",
          answer: "Use the 'Fee Dues Report' in the Reports section. This shows all students with pending fee payments, overdue amounts, and days overdue."
        },
        {
          question: "Can I export data to Excel?",
          answer: "Yes, most reports and data tables have export options. Look for 'Export Excel' or 'Export CSV' buttons in reports and data management sections."
        }
      ]
    },
    {
      category: "User Management",
      questions: [
        {
          question: "How do I add new staff members?",
          answer: "Go to Staff Management, click 'Add New Staff', fill in personal and professional details, assign a role, set permissions, and create login credentials."
        },
        {
          question: "How do I manage user permissions?",
          answer: "In Role Management, you can create custom roles and assign specific permissions. Then assign these roles to staff members to control their access to different features."
        },
        {
          question: "How do I reset a user's password?",
          answer: "In Staff Management, find the user and click 'Edit', then use the 'Reset Password' option to generate a new temporary password that the user can change on first login."
        }
      ]
    },
    {
      category: "Technical Issues",
      questions: [
        {
          question: "The system is running slowly. What should I do?",
          answer: "Try clearing your browser cache, ensure you have a stable internet connection, and contact support if the issue persists. We can help optimize the system performance."
        },
        {
          question: "I'm getting an error when uploading files. What's wrong?",
          answer: "Check that your file is in the correct format (PDF, JPG, PNG for most uploads), under the size limit (usually 5MB), and try again. If the error persists, contact technical support."
        },
        {
          question: "How do I backup my data?",
          answer: "Regular backups are automatically taken. For manual backups, go to System Settings > Backup and click 'Create Backup'. Contact support for data restoration if needed."
        }
      ]
    }
  ];

  const quickLinks = [
    {
      title: "User Manual",
      description: "Complete guide to using the system",
      icon: "bi-book",
      color: "primary"
    },
    {
      title: "Video Tutorials",
      description: "Step-by-step video guides",
      icon: "bi-play-circle",
      color: "success"
    },
    {
      title: "API Documentation",
      description: "For developers and integrations",
      icon: "bi-code-slash",
      color: "info"
    },
    {
      title: "Keyboard Shortcuts",
      description: "Speed up your workflow",
      icon: "bi-keyboard",
      color: "warning"
    }
  ];

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-0">
            <i className="bi bi-question-circle me-2"></i>
            Help & Support
          </h4>
          <small className="text-muted">
            Find answers to common questions and get help
          </small>
        </div>
        <Button 
          variant="primary"
          onClick={() => setShowContactModal(true)}
        >
          <i className="bi bi-headset me-2"></i>
          Contact Support
        </Button>
      </div>

      <Row className="mb-4 g-3">
        <Col md={3} sm={6}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <small className="text-muted d-block">Total Tickets</small>
              <div className="display-6 text-primary mt-2">{ticketSummary.total}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <small className="text-muted d-block">Open</small>
              <div className="display-6 text-info mt-2">{ticketSummary.open}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <small className="text-muted d-block">Resolved</small>
              <div className="display-6 text-success mt-2">{ticketSummary.resolved}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <small className="text-muted d-block">High / Urgent</small>
              <div className="display-6 text-danger mt-2">{ticketSummary.high + ticketSummary.urgent}</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="border-0 shadow-sm mb-4">
        <Card.Header className="bg-white py-3">
          <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3">
            <div>
              <h5 className="mb-1">Support Tickets</h5>
              <small className="text-muted">View, respond, and manage all support requests</small>
            </div>
            <InputGroup className="w-100 w-lg-auto">
              <InputGroup.Text className="bg-white"><i className="bi bi-search"></i></InputGroup.Text>
              <Form.Control
                placeholder="Search subject or message"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </InputGroup>
          </div>
        </Card.Header>
        <Card.Body>
          <Row className="g-3 mb-3">
            <Col md={3} sm={6}>
              <Form.Select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </Form.Select>
            </Col>
            <Col md={3} sm={6}>
              <Form.Select
                value={filters.priority}
                onChange={(e) => handleFilterChange('priority', e.target.value)}
              >
                <option value="">All Priorities</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </Form.Select>
            </Col>
            <Col md={3} sm={6}>
              <Form.Select
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
              >
                <option value="">All Categories</option>
                <option value="general">General</option>
                <option value="technical">Technical</option>
                <option value="billing">Billing</option>
                <option value="data">Data</option>
                <option value="integration">Integration</option>
                <option value="other">Other</option>
              </Form.Select>
            </Col>
            <Col md={3} sm={6}>
              <Form.Select
                value={filters.user_type}
                onChange={(e) => handleFilterChange('user_type', e.target.value)}
              >
                <option value="">All User Types</option>
                <option value="admin">Admin</option>
                <option value="staff">Staff</option>
                <option value="parent">Parent</option>
              </Form.Select>
            </Col>
          </Row>

          {ticketsLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : tickets.length === 0 ? (
            <Alert variant="secondary" className="text-center mb-0">
              <i className="bi bi-inbox display-5 text-muted d-block mb-3"></i>
              No support tickets found for the selected filters.
            </Alert>
          ) : (
            <div className="table-responsive">
              <Table hover className="align-middle">
                <thead className="table-light">
                  <tr>
                    <th>ID</th>
                    <th>Subject</th>
                    <th>Category</th>
                    <th>Priority</th>
                    <th>Status</th>
                    <th>User</th>
                    <th>Created</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map((ticket) => (
                    <tr key={ticket.id}>
                      <td><Badge bg="secondary">#{ticket.id}</Badge></td>
                      <td>
                        <div className="fw-semibold">{ticket.subject}</div>
                        <small className="text-muted d-block text-truncate" style={{ maxWidth: 280 }}>
                          {ticket.message}
                        </small>
                      </td>
                      <td className="text-capitalize">{ticket.category || 'general'}</td>
                      <td>
                        <Badge bg={priorityBadgeVariant(ticket.priority)} className="text-uppercase">
                          {ticket.priority || 'medium'}
                        </Badge>
                      </td>
                      <td>
                        <Badge bg={statusBadgeVariant(ticket.status)} className="text-capitalize">
                          {(ticket.status || 'open').replace('_', ' ')}
                        </Badge>
                      </td>
                      <td>
                        <div className="fw-semibold">{ticket.user_name || 'Unknown'}</div>
                        <small className="text-muted text-uppercase">{ticket.user_type || 'N/A'}</small>
                      </td>
                      <td>
                        <small className="text-muted">{ticket.created_at ? new Date(ticket.created_at).toLocaleString() : '-'}</small>
                      </td>
                      <td className="text-end">
                        <div className="btn-group" role="group">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => openResponseModal(ticket)}
                          >
                            <i className="bi bi-reply"></i>
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => {
                              if (window.confirm('Are you sure you want to delete this support ticket?')) {
                                deleteTicketMutation.mutate(ticket.id);
                              }
                            }}
                          >
                            <i className="bi bi-trash"></i>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}

          {totalTickets > 0 && (
            <div className="mt-3">
              <Pagination
                currentPage={currentPage}
                totalItems={totalTickets}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={(newSize) => {
                  setItemsPerPage(newSize);
                  setCurrentPage(1);
                }}
              />
            </div>
          )}
        </Card.Body>
      </Card>

      <Row className="mb-4">
        <Col md={6}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body className="text-center">
              <i className="bi bi-chat-dots display-4 text-primary mb-3"></i>
              <h5>Need Help?</h5>
              <p className="text-muted">
                Our support team is here to help you with any questions or issues you may have.
              </p>
              <Button 
                variant="primary"
                onClick={() => setShowContactModal(true)}
              >
                Contact Support
              </Button>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body className="text-center">
              <i className="bi bi-telephone display-4 text-success mb-3"></i>
              <h5>Emergency Contact</h5>
              <p className="text-muted mb-2">
                For urgent technical issues or system downtime
              </p>
              <div className="mb-2">
                <strong>Phone:</strong> +91 98765 43210
              </div>
              <div>
                <strong>Email:</strong> support@schoolsystem.com
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        {quickLinks.map((link, index) => (
          <Col md={3} key={index} className="mb-3">
            <Card className="border-0 shadow-sm h-100 hover-card">
              <Card.Body className="text-center">
                <i className={`${link.icon} display-6 text-${link.color} mb-3`}></i>
                <h6>{link.title}</h6>
                <p className="text-muted small">{link.description}</p>
                <Button variant={`outline-${link.color}`} size="sm">
                  View <i className="bi bi-arrow-right ms-1"></i>
                </Button>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <Card className="border-0 shadow-sm">
        <Card.Header>
          <h5 className="mb-0">
            <i className="bi bi-question-circle me-2"></i>
            Frequently Asked Questions
          </h5>
        </Card.Header>
        <Card.Body>
          <Accordion defaultActiveKey="0">
            {faqData.map((category, categoryIndex) => (
              <div key={categoryIndex} className="mb-4">
                <h6 className="text-primary mb-3">
                  <i className="bi bi-folder2-open me-2"></i>
                  {category.category}
                </h6>
                {category.questions.map((faq, faqIndex) => (
                  <Accordion.Item 
                    eventKey={`${categoryIndex}-${faqIndex}`} 
                    key={faqIndex}
                    className="mb-2"
                  >
                    <Accordion.Header>
                      <i className="bi bi-question-circle-fill me-2 text-info"></i>
                      {faq.question}
                    </Accordion.Header>
                    <Accordion.Body>
                      {faq.answer}
                    </Accordion.Body>
                  </Accordion.Item>
                ))}
              </div>
            ))}
          </Accordion>
        </Card.Body>
      </Card>

      <Row className="mt-4">
        <Col md={4}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <i className="bi bi-info-circle display-6 text-info mb-3"></i>
              <h6>System Information</h6>
              <div className="text-start">
                <div className="mb-2">
                  <small className="text-muted">Version:</small>
                  <div>v2.1.0</div>
                </div>
                <div className="mb-2">
                  <small className="text-muted">Last Updated:</small>
                  <div>August 2025</div>
                </div>
                <div className="mb-2">
                  <small className="text-muted">License:</small>
                  <div>Enterprise</div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <i className="bi bi-shield-check display-6 text-success mb-3"></i>
              <h6>Security & Privacy</h6>
              <p className="text-muted small">
                Your data is protected with enterprise-grade security measures.
              </p>
              <Button variant="outline-success" size="sm">
                View Security Policy
              </Button>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <i className="bi bi-star display-6 text-warning mb-3"></i>
              <h6>Feature Requests</h6>
              <p className="text-muted small">
                Have an idea for improving the system? Let us know!
              </p>
              <Button variant="outline-warning" size="sm">
                Submit Idea
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Contact Support Modal */}
      <Modal show={showContactModal} onHide={() => setShowContactModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-headset me-2"></i>
            Contact Support
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleContactSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="text-muted">Subject</Form.Label>
                  <Form.Control
                    type="text"
                    value={contactForm.subject}
                    onChange={(e) => handleInputChange('subject', e.target.value)}
                    required
                    placeholder="Brief description of your issue"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="text-muted">Category</Form.Label>
                  <Form.Select
                    value={contactForm.category}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                  >
                    <option value="general">General Question</option>
                    <option value="technical">Technical Issue</option>
                    <option value="billing">Billing & Account</option>
                    <option value="feature">Feature Request</option>
                    <option value="bug">Bug Report</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label className="text-muted">Priority</Form.Label>
              <Form.Select
                value={contactForm.priority}
                onChange={(e) => handleInputChange('priority', e.target.value)}
              >
                <option value="low">Low - General inquiry</option>
                <option value="medium">Medium - Standard issue</option>
                <option value="high">High - Important issue</option>
                <option value="urgent">Urgent - System down</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="text-muted">Message</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={contactForm.message}
                onChange={(e) => handleInputChange('message', e.target.value)}
                required
                placeholder="Please describe your issue or question in detail..."
              />
            </Form.Group>

            <Alert variant="info">
              <i className="bi bi-info-circle me-2"></i>
              <strong>Response Time:</strong> We typically respond within 24 hours for standard issues, and within 2 hours for urgent issues during business hours.
            </Alert>

            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={() => setShowContactModal(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={submitTicketMutation.isLoading}>
                {submitTicketMutation.isLoading ? 'Submitting...' : 'Submit Ticket'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal show={showResponseModal} onHide={closeResponseModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-reply-fill me-2"></i>
            Respond to Ticket
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTicket && (
            <Form onSubmit={handleResponseSubmit}>
              <Row className="mb-3">
                <Col md={8}>
                  <Form.Group>
                    <Form.Label className="text-muted">Subject</Form.Label>
                    <Form.Control value={selectedTicket.subject} disabled />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="text-muted">Ticket ID</Form.Label>
                    <Form.Control value={`#${selectedTicket.id}`} disabled />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label className="text-muted">Original Message</Form.Label>
                <Form.Control as="textarea" rows={4} value={selectedTicket.message} disabled />
              </Form.Group>

              <Row className="g-3 mb-3">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="text-muted">Status</Form.Label>
                    <Form.Select
                      value={responseForm.status}
                      onChange={(e) => setResponseForm(prev => ({ ...prev, status: e.target.value }))}
                    >
                      <option value="open">Open</option>
                      <option value="in_progress">In Progress</option>
                      <option value="resolved">Resolved</option>
                      <option value="closed">Closed</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="text-muted">Priority</Form.Label>
                    <Form.Select
                      value={responseForm.priority}
                      onChange={(e) => setResponseForm(prev => ({ ...prev, priority: e.target.value }))}
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="text-muted">User Type</Form.Label>
                    <Form.Control value={selectedTicket.user_type || 'N/A'} disabled />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label className="text-muted">Response</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={responseForm.response}
                  onChange={(e) => setResponseForm(prev => ({ ...prev, response: e.target.value }))}
                  placeholder="Write your response here..."
                />
              </Form.Group>

              <div className="d-flex justify-content-end gap-2">
                <Button variant="secondary" onClick={closeResponseModal}>Cancel</Button>
                <Button type="submit" variant="primary" disabled={updateTicketMutation.isLoading}>
                  {updateTicketMutation.isLoading ? 'Saving...' : 'Save Response'}
                </Button>
              </div>
            </Form>
          )}
        </Modal.Body>
      </Modal>

      <style jsx>{`
        .hover-card {
          transition: transform 0.2s ease-in-out;
        }
        .hover-card:hover {
          transform: translateY(-2px);
        }
      `}</style>
    </div>
  );
};

export default HelpSupport;